import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    Paper,
    Snackbar,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import planService from '../services/PlanService';
import { features as featureRegistry } from '../appConfig';

function normalizeString(value) {
    return String(value ?? '').trim();
}

function uniqueStrings(list) {
    const out = [];
    const seen = new Set();
    (Array.isArray(list) ? list : []).forEach((v) => {
        const s = normalizeString(v);
        if (!s) return;
        if (seen.has(s)) return;
        seen.add(s);
        out.push(s);
    });
    return out;
}

function buildFeatureOptions(registry) {
    const entries = registry && typeof registry === 'object' ? Object.entries(registry) : [];
    return entries
        .map(([key, description]) => ({
            key,
            description: normalizeString(description),
            label: (() => {
                const desc = normalizeString(description);
                if (!desc) return key;
                // Prefer a human-friendly label (no feature key displayed).
                return desc
                    .replace(/^Enable\s+/i, '')
                    .replace(/^Allow\s+/i, '')
                    .replace(/^Option\s+to\s+/i, '')
                    .trim();
            })(),
        }))
        .sort((a, b) => a.key.localeCompare(b.key));
}

function PlanDialog({ open, mode, initialPlan, featureOptions, onClose, onSubmit, submitting }) {
    const { t } = useTranslation();
    const [planName, setPlanName] = useState('');
    const [description, setDescription] = useState('');
    const [upgradeable, setUpgradeable] = useState(false);
    const [selectedFeatureKeys, setSelectedFeatureKeys] = useState([]);
    const [unknownFeatureKeys, setUnknownFeatureKeys] = useState([]);

    useEffect(() => {
        if (!open) return;
        const plan = initialPlan || {};
        setPlanName(normalizeString(plan.planName));
        setDescription(normalizeString(plan.description));
        setUpgradeable(Boolean(plan.upgradeable));
        const current = uniqueStrings(plan.features);
        setSelectedFeatureKeys(current);
        const unknown = current.filter((k) => !featureOptions.some((o) => o.key === k)).sort((a, b) => a.localeCompare(b));
        setUnknownFeatureKeys(unknown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialPlan?.planId]);

    const title = mode === 'edit' ? t('plans.editPlan', 'Edit Plan') : t('plans.newPlan', 'New Plan');

    const handleSubmit = async () => {
        const payload = {
            planName: normalizeString(planName),
            description: normalizeString(description),
            upgradeable,
            features: uniqueStrings(selectedFeatureKeys),
        };

        await onSubmit(payload);
    };

    const canSubmit = Boolean(normalizeString(planName));

    const selectedSet = useMemo(() => new Set(selectedFeatureKeys), [selectedFeatureKeys]);

    const toggleFeature = (key) => {
        const k = normalizeString(key);
        if (!k) return;
        setSelectedFeatureKeys((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return Array.from(next).sort((a, b) => a.localeCompare(b));
        });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    pt: 3,
                    // Prevent the outlined field's notch/label from being clipped.
                    overflowY: 'visible',
                }}
            >
                <TextField
                    label={t('plans.planName', 'Plan Name')}
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    required
                    fullWidth
                    disabled={submitting}
                    sx={{ mt: 0.5 }}
                />
                <TextField
                    label={t('plans.description', 'Description')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    disabled={submitting}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={upgradeable}
                            onChange={(e) => setUpgradeable(e.target.checked)}
                            disabled={submitting}
                        />
                    }
                    label={t('plans.upgradeable', 'Upgradeable')}
                />

                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {t('plans.features', 'Features')}
                    </Typography>
                    <Box sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        maxHeight: 260,
                        overflow: 'auto',
                        px: 1,
                        py: 0.5,
                    }}>
                        <FormGroup>
                            {featureOptions.map((opt) => (
                                <FormControlLabel
                                    key={opt.key}
                                    control={
                                        <Switch
                                            checked={selectedSet.has(opt.key)}
                                            onChange={() => toggleFeature(opt.key)}
                                            disabled={submitting}
                                        />
                                    }
                                    label={opt.label || opt.key}
                                />
                            ))}
                        </FormGroup>

                        {unknownFeatureKeys.length > 0 ? (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {t('plans.unknownFeatures', 'Unknown features (from existing plan)')}
                                </Typography>
                                <FormGroup>
                                    {unknownFeatureKeys.map((k) => (
                                        <FormControlLabel
                                            key={k}
                                            control={
                                                <Switch
                                                    checked={selectedSet.has(k)}
                                                    onChange={() => toggleFeature(k)}
                                                    disabled={submitting}
                                                />
                                            }
                                            label={k}
                                        />
                                    ))}
                                </FormGroup>
                            </>
                        ) : null}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting}>{t('plans.cancel', 'Cancel')}</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || submitting}>
                    {mode === 'edit' ? t('plans.update', 'Update') : t('plans.create', 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function Plans() {
    const { t } = useTranslation();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [editingPlan, setEditingPlan] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const featureOptions = useMemo(() => buildFeatureOptions(featureRegistry), []);

    const loadPlans = useCallback(async () => {
        setLoading(true);
        try {
            const data = await planService.getPlans();
            setPlans(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load plans', e);
            setSnackbar({ open: true, message: t('plans.failedToLoad', 'Failed to load plans'), severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPlans();
    }, [loadPlans]);

    const sortedPlans = useMemo(() => {
        const list = Array.isArray(plans) ? [...plans] : [];
        list.sort((a, b) => normalizeString(a?.planName).localeCompare(normalizeString(b?.planName)));
        return list;
    }, [plans]);

    const openCreate = () => {
        setDialogMode('create');
        setEditingPlan(null);
        setDialogOpen(true);
    };

    const openEdit = (plan) => {
        setDialogMode('edit');
        setEditingPlan(plan || null);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
    };

    const handleSubmitDialog = async (payload) => {
        setSaving(true);
        try {
            if (dialogMode === 'edit') {
                const planId = editingPlan?.planId;
                await planService.updatePlan(planId, {
                    ...(editingPlan || {}),
                    ...payload,
                    planId,
                });
                setSnackbar({ open: true, message: t('plans.planUpdated', 'Plan updated'), severity: 'success' });
            } else {
                await planService.createPlan(payload);
                setSnackbar({ open: true, message: t('plans.planCreated', 'Plan created'), severity: 'success' });
            }
            setDialogOpen(false);
            await loadPlans();
        } catch (e) {
            console.error('Failed to save plan', e);
            setSnackbar({ open: true, message: t('plans.failedToSave', 'Failed to save plan'), severity: 'error' });
            throw e;
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (plan) => {
        const planId = plan?.planId;
        const name = normalizeString(plan?.planName) || planId || t('plans.thisPlan', 'this plan');
        if (!planId) return;

        const ok = window.confirm(t('plans.deleteConfirm', 'Delete {{name}}?', { name }));
        if (!ok) return;

        setSaving(true);
        try {
            await planService.deletePlan(planId);
            setSnackbar({ open: true, message: t('plans.planDeleted', 'Plan deleted'), severity: 'success' });
            await loadPlans();
        } catch (e) {
            console.error('Failed to delete plan', e);
            setSnackbar({ open: true, message: t('plans.failedToDelete', 'Failed to delete plan'), severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const headCellSx = {
        fontWeight: 700,
        fontSize: '0.75rem',
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: '#8a9bb5',
        backgroundColor: '#131926',
        borderBottom: '2px solid rgba(40,123,214,0.35)',
        py: 1.5,
    };

    return (
        <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: '#287bd6', flexShrink: 0 }} />
                <Box sx={{ mr: 'auto' }}>
                    <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: '#1e2030', lineHeight: 1.2 }}>{t('sideBar.plans', 'Plans')}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7a99' }}>{t('plans.records', '{{num}} records', { num: sortedPlans.length })}</Typography>
                </Box>
                <Button
                    variant="contained"
                    onClick={openCreate}
                    disabled={loading || saving}
                    sx={{
                        borderRadius: '8px',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #287bd6 0%, #1f6fca 100%)',
                        boxShadow: '0 2px 10px rgba(40,123,214,0.35)',
                        '&:hover': { background: 'linear-gradient(135deg, #3f8fe0 0%, #287bd6 100%)' },
                    }}
                >
                    {t('plans.newPlan', 'New Plan')}
                </Button>
            </Box>

            {loading ? <Alert severity="info">{t('plans.loading', 'Loading plans…')}</Alert> : null}

            {!loading && sortedPlans.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed rgba(0,0,0,0.13)', borderRadius: '10px' }}>
                    <Typography variant="body1" sx={{ color: '#6b7a99' }}>{t('plans.noPlans', 'No plans found.')}</Typography>
                </Paper>
            ) : (
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: '1.5px solid rgba(0,0,0,0.13)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headCellSx}>{t('plans.colPlanId', 'Plan ID')}</TableCell>
                                <TableCell sx={headCellSx}>{t('plans.planName', 'Plan Name')}</TableCell>
                                <TableCell sx={headCellSx}>{t('plans.upgradeable', 'Upgradeable')}</TableCell>
                                <TableCell sx={headCellSx}>{t('plans.description', 'Description')}</TableCell>
                                <TableCell align="right" sx={headCellSx}>{t('plans.colActions', 'Actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedPlans.map((p) => (
                                <TableRow
                                    key={p.planId || p.planName}
                                    sx={{
                                        transition: 'background-color 0.15s ease',
                                        '&:hover td': { backgroundColor: 'rgba(40,123,214,0.18)', transition: 'background-color 0.15s ease' },
                                        '&:last-child td': { borderBottom: 0 },
                                    }}
                                >
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.82rem', color: '#6b7a99' }}>{p.planId || '-'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: '#1e2030' }}>{p.planName || '-'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap', color: p.upgradeable ? '#287bd6' : '#8a9bb5', fontWeight: 600 }}>
                                        {p.upgradeable ? t('plans.yes', 'Yes') : t('plans.no', 'No')}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 520, color: '#5a6275', fontSize: '0.85rem' }}>{p.description || ''}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Button size="small" onClick={() => openEdit(p)} disabled={saving}
                                            sx={{ borderRadius: '6px', fontWeight: 600, color: '#1e2030', '&:hover': { backgroundColor: 'rgba(40,123,214,0.08)', color: '#287bd6' } }}>
                                            {t('plans.edit', 'Edit')}
                                        </Button>
                                        <Button size="small" onClick={() => handleDelete(p)} disabled={saving}
                                            sx={{ borderRadius: '6px', fontWeight: 600, color: '#287bd6', '&:hover': { backgroundColor: 'rgba(40,123,214,0.08)' } }}>
                                            {t('plans.delete', 'Delete')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <PlanDialog
                open={dialogOpen}
                mode={dialogMode}
                initialPlan={editingPlan}
                featureOptions={featureOptions}
                onClose={closeDialog}
                onSubmit={handleSubmitDialog}
                submitting={saving}
            />

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Plans;
